import React from 'react';
import { useDispatch } from 'react-redux';
import moment from 'moment';
import { Buffer } from 'buffer';
import Alert from 'react-bootstrap/Alert';
import Modal from 'react-bootstrap/Modal';
import ProgressBar from 'react-bootstrap/ProgressBar';
import { strings } from '../../resources/strings';
import { SeparationType } from '../../enums/SeparationType';
import { ProfileType } from '../../enums/ProfileType';
import { PeriodTypeForOrdersInTransport } from '../../enums/PeriodTypeForOrdersInTransport';
import { ShopLocation } from '../../models/ShopLocation';
import { Carrier } from '../../models/Carrier';
import { MainActions } from '../../actions/MainActions';
import { useAuth } from '../../contexts/AuthContext';
import ServicesContext from '../../contexts/ServicesContext';
import { ManageTransportHelper } from '../../helpers/ManageTransportHelper';
import { GetOrdersInTransportInput } from '../../services/Orders/GetOrdersInTransport/GetOrdersInTransportInput';
import { GenerateTransportOrderBaseReportInput } from '../../services/Reports/GenerateTransportOrderBaseReport/GenerateTransportOrderBaseReportInput';
import { orderDeliveryStates } from '../../services/StatesOrders/StatesService';
import { Button } from '../../common-components/Button/Button';
import { Field, FieldType } from '../../common-components/Field/Field';
import { If } from '../../common-components/If/If';
import { FieldGroup, FieldLabel, Row } from './styles';

interface IModalTransportOrderBaseReportProps {
  close: () => void;
}

const ReportAlert = () => (
  <Alert variant='warning'>
    <strong>Atenção!</strong> Como o relatório precisa de vários dados dos pedidos a geração pode demorar demasiadamente.
  </Alert>
);

const ModalTransportOrderBaseReport: React.FC<IModalTransportOrderBaseReportProps> = ({
  close
}) => {

  const step = 50;

  const orderStateFilterValues = ManageTransportHelper.getOrderStateFilterValues();

  const dispatch = useDispatch();
  const mainActions = React.useMemo(() => new MainActions(dispatch), [dispatch]);

  const { userLocations, session } = useAuth();

  const [initialDate, setInitialDate] = React.useState<Date | null>(new Date());
  const [finalDate, setFinalDate] = React.useState<Date | null>(new Date());
  const [locationId, setLocationId] = React.useState<number | null>(userLocations[0]);
  const [locations, setLocations] = React.useState<ShopLocation[]>([]);
  const [loadingLocations, setLoadingLocations] = React.useState<boolean>(true);
  const [separationType, setSeparationType] = React.useState<number | null>(
    session?.user?.profileType === ProfileType.Vendor ? SeparationType.Prescription : 0);
  const [carrierCode, setCarrierCode] = React.useState<string | undefined>('');
  const [stateIds, setStateIds] = React.useState<string | undefined>('');
  const [stateFilterCode, setStateFilterCode] = React.useState<string | undefined>(orderStateFilterValues[0].code);
  const [periodType, setPeriodType] = React.useState<PeriodTypeForOrdersInTransport | null>(PeriodTypeForOrdersInTransport.OrderDispatched);
  const [carrierList, setCarrierList] = React.useState<Carrier[]>([]);
  const [loadingCarrierList, setLoadingCarrierList] = React.useState<boolean>(true);
  const [includesCrossChannelSale, setIncludesCrossChannelSale] = React.useState<boolean>(true);

  const [generating, setGenerating] = React.useState<boolean>(false);
  const [canceling, setCanceling] = React.useState<boolean>(false);
  const [percent, setPercent] = React.useState<number>(0);

  const orderIdList = React.useRef<number[] | null>(null);
  const stringDataList = React.useRef<string[]>([]);
  const cancel = React.useRef(false);

  const {
    generateTransportOrderBaseReportService,
    getOrderIdListForTransportOrderBaseReportService,
    toastService,
    reportService,
    statesService,
    getLocationListService,
    getCarrierListService
  } = React.useContext(ServicesContext);

  React.useEffect(() => {
    getLocations();
    getCarrierList();
  }, []);

  const memoBody = React.useMemo(() => {
    return (
      <>
        <If condition={generating}>
          <FieldGroup marginBottom='10px'>
            <FieldLabel>Aguarde, gerando o relatório...</FieldLabel>
            <ProgressBar now={percent} label={`${percent}%`} variant='success' />
          </FieldGroup>
          <ReportAlert />
        </If>
        <If condition={!generating}>
          <Row>
            <Field
              type={FieldType.Select}
              label={'Tipo do período de abrangência'}
              selectProps={{
                options: [
                  {
                    id: PeriodTypeForOrdersInTransport.LastPackageStatus,
                    name: ManageTransportHelper.getDescriptionOfPeriodTypeForOrdersInTransport(PeriodTypeForOrdersInTransport.LastPackageStatus)
                  },
                  {
                    id: PeriodTypeForOrdersInTransport.OrderDispatched,
                    name: ManageTransportHelper.getDescriptionOfPeriodTypeForOrdersInTransport(PeriodTypeForOrdersInTransport.OrderDispatched)
                  }
                ],
                displayField: 'name',
                keyField: 'id'
              }}
              value={periodType}
              change={(value) => setPeriodType(+value)}
              marginTop={'0px'}
            />
          </Row>
          <FieldGroup>
            <FieldLabel>Período de abrangência do relatório</FieldLabel>
            <Row>
              <Field
                type={FieldType.DatePicker}
                value={initialDate}
                change={(value) => setInitialDate(value)}
                marginTop='0px'
                marginRight='5px'
              />
              <Field
                type={FieldType.DatePicker}
                value={finalDate}
                change={(value) => setFinalDate(value)}
                marginTop='0px'
                marginLeft='5px'
              />
            </Row>
          </FieldGroup>
          <Row>
            {loadingCarrierList ? null : (
            <Field
              type={FieldType.Select}
              label={strings.Carrier}
              selectProps={{
                options: [{ code: '', name: strings.All }, ...carrierList],
                displayField: 'name',
                keyField: 'code'
              }}
              value={carrierCode ? carrierCode : ''}
              change={(value) => setCarrierCode(value)}
              marginTop={'0px'}
              marginRight='5px'
            />
            )}
            <Field
              type={FieldType.Select}
              label={strings.StateOfOrder}
              selectProps={{
                options: [ ...orderStateFilterValues ],
                displayField: 'display',
                keyField: 'code'
              }}
              value={stateFilterCode || orderStateFilterValues[0].code}
              change={(value) => setStateFilterCode(value)}
              marginTop={'0px'}
              marginLeft='5px'
            />
          </Row>
          <Row>
            {loadingLocations ? null : (
            <Field
              type={FieldType.Select}
              label={strings.Location}
              selectProps={{
                options: userLocations.length > 1 
                  ? [...locations.filter(l => userLocations.some(ulid => ulid === l.id)), { id:0, name:'Todas' }]
                  : [...locations.filter(l => userLocations.some(ulid => ulid === l.id))],
                displayField: 'name',
                keyField: 'id'
              }}
              value={locationId}
              change={(value) => setLocationId(value)}
              marginTop={'0px'}
              marginRight='5px'
              disabled={userLocations.length <= 1}
            />
            )}
            <Field
              type={FieldType.Select}
              label={strings.SeparationType}
              selectProps={{
                options: [
                  { id: 0, name: 'Todos' },
                  { id: SeparationType.Prescription, name: 'Com Grau' },
                  { id: SeparationType.NonPrescription, name: 'Sem Grau' },
                  { id: SeparationType.HomeTryOn, name: 'Zerezes em Casa' },
                ],
                displayField: 'name',
                keyField: 'id'
              }}
              value={separationType}
              change={(value) => setSeparationType(value)}
              marginTop={'0px'}
              marginLeft='5px'
            />
          </Row>
          <Field 
            type={FieldType.CheckBox} 
            label='Incluir pedidos omni'
            value={includesCrossChannelSale}
            change={(value) => setIncludesCrossChannelSale(value)}
            marginTop={'0px'}
          />
          <ReportAlert />
        </If>
      </>
    )
  }, [
    generating, canceling, percent,
    initialDate, finalDate, carrierCode, stateIds, locationId, separationType,
    periodType, loadingLocations, locations, loadingCarrierList, carrierList,
    stateFilterCode
  ]);

  const memoFooter = React.useMemo(() => {
    return (
      <>
        <If condition={generating}>
          <Button text={strings.Cancel} disabled={!generating || canceling} click={() => cancelReport()} />
        </If>
        <If condition={!generating}>
          <Button text={strings.Confirm} click={() => confirmReport()} />
        </If>
      </>
    )
  }, [
    generating, canceling, percent,
    initialDate, finalDate, carrierCode, stateIds, locationId, separationType,
    periodType, loadingLocations, locations, loadingCarrierList, carrierList,
    stateFilterCode
  ]);

  return (
    <Modal
      show={true}
      onHide={() => {
        if (generating || canceling) return;
        closeModal();
      }}
      backdrop="static"
      keyboard={false}
    >
      <Modal.Header closeButton>
        <Modal.Title style={{ fontSize: '1.3em' }}>
          {strings.TransportOrderBaseReport}
        </Modal.Title>
      </Modal.Header>

      <Modal.Body>
        {memoBody}
      </Modal.Body>

      <Modal.Footer style={{ justifyContent: 'center' }}>
        {memoFooter}
      </Modal.Footer>
    </Modal>
  );

  function closeModal() {
    mainActions.setNotWait(false);
    close();
  }

  function createInput() {
    const input = new GetOrdersInTransportInput();
    const selectedLocationId = locationId && +locationId > 0 ? +locationId : null;
    input.shippingStartDate = moment(initialDate).format('YYYY-MM-DD');
    input.shippingEndDate = moment(finalDate).format('YYYY-MM-DD');
    input.locationId = selectedLocationId;
    input.separationType = separationType && +separationType > 0 ? +separationType : null;
    input.carrierCode = carrierCode;
    input.stateFilterCode = stateFilterCode;
    const statesId = orderStateFilterValues.find(x => x.code == stateFilterCode)?.states || orderStateFilterValues[0].states;
    input.stateIds = statesId && statesId.length > 0 ? statesId.join(' ') : undefined;
    input.shouldPaginate = false;
    input.includeDeliveredOrders = stateIds ? +stateIds.split(' ')[0] === -1 : false;
    input.periodType = periodType;
    input.onlyCrossChannelSale = false;
    input.includesCrossChannelSale = false;
    input.fulfilledBy = null;
    input.fulfillmentLocationIds = null;
    if (includesCrossChannelSale) {
      input.includesCrossChannelSale = true;
      input.fulfilledBy = 'mystore_and_ecommerce';
      if (session?.user?.profileType == ProfileType.Vendor) {
        input.fulfillmentLocationIds = [1];
      }
    }
    input.userId = session?.user?.id;
    return input;
  }

  function confirmReport() {
    setGenerating(true);
    setPercent(0);
    setCanceling(false);

    mainActions.setNotWait(true);

    stringDataList.current = [];
    cancel.current = false;

    const input = createInput();

    getOrderIdListForTransportOrderBaseReportService.handle(input)
      .then(result => {
        orderIdList.current = result?.value || [];
        if (orderIdList.current.length === 0) {
          toastService.info('Relatório vazio!');
          setGenerating(false);
          return;
        }
        generateProgressiveReport(0, 0);
      })
      .catch(error => {
        console.log('Error in confirmReport:', error);
        setGenerating(false);
        notifyProblemWhenGenerateReport();
      });
  }

  function generateProgressiveReport(index: number, errors: number) {
    if (cancel.current || !orderIdList.current) {
      setGenerating(false);
      return;
    }

    if (index > orderIdList.current.length) {
      try {
        setPercent(100);
        setTimeout(() => downloadReport(), 1000);
      } catch (error) {
        console.log('Error in generateProgressiveReport:', error);
        notifyProblemWhenGenerateReport();
      }
      return;
    }

    if (errors > 3) {
      setGenerating(false);
      notifyProblemWhenGenerateReport();
      return;
    }

    const ids = orderIdList.current.slice(index, step + index);

    if (ids.length === 0) {
      generateProgressiveReport(step + index, 0);
      return;
    }

    let newPercent = (index / orderIdList.current.length) * 100;
    newPercent = +newPercent.toFixed(0);
    setPercent(newPercent);

    const input = new GenerateTransportOrderBaseReportInput();
    input.filter = createInput();
    input.filter.orderIds = ids.join(',');
    input.includeHeaders = index === 0;
    input.returnStringData = true;
    generateTransportOrderBaseReportService.handleCsvOrders(input)
      .then(result => {
        stringDataList.current.push(result?.value?.stringData || '');
        setTimeout(() => generateProgressiveReport(step + index, 0), 500);
      })
      .catch(error => {
        console.log('Error in generateTransportOrderBaseReportService:', error);
        setTimeout(() => generateProgressiveReport(index, errors + 1), 1000);
      });
  }

  function downloadReport() {
    const csvFile = stringDataList.current.map(value => Buffer.from(value, 'base64').toString()).join('');
    const selectedLocationId = locationId && +locationId > 0 ? +locationId : null;

    const separationTypeValue = separationType ? +separationType : null;
    let separationTypeDescription = '';
    switch (separationTypeValue) {
      case SeparationType.Prescription:
        separationTypeDescription = 'com grau';
        break;
      case SeparationType.NonPrescription:
        separationTypeDescription = 'não grau';
        break;
      case SeparationType.HomeTryOn:
        separationTypeDescription = 'zerezes em casa';
        break;
      default:
        separationTypeDescription = '';
    }

    const reportName = 'Relatório Base de Pedidos em Transporte' +
      (carrierCode ? ' por ' + carrierList.find(c => c.code === carrierCode)?.name : '') +
      (selectedLocationId ? ' da loja ' + locations.find(l => +l.id === selectedLocationId)?.name : '') +
      (separationTypeDescription ? ` do tipo ${separationTypeDescription}` : '') +
      (stateFilterCode ? ' no estado ' + orderStateFilterValues.find(x => x.code == stateFilterCode)?.display : '') +
      (initialDate ? ' a partir de ' + moment(initialDate).format('DD-MM-YYYY') : '') +
      (finalDate ? ' até ' + moment(finalDate).format('DD-MM-YYYY') : '');

    reportService.downloadCsvFromString(csvFile, `${reportName}.csv`);

    setGenerating(false);
    closeModal();
  }

  function notifyProblemWhenGenerateReport() {
    mainActions.setNotWait(false);
    toastService.warning('Não consegui gerar o relatório. Tente novamente mais tarde e se o problema persistir avise o time Tech.');
  }

  function cancelReport() {
    setCanceling(true);
    cancel.current = true;
    mainActions.setNotWait(false);
  }

  function getLocations() {
    setLoadingLocations(true);
    mainActions.setNotWait(true);
    getLocationListService.handle()
      .then(result => setLocations(result?.value?.locations || []))
      .finally(() => {
        setTimeout(() => mainActions.setNotWait(false), 1000);
        setLoadingLocations(false);
      });
  }

  function getCarrierList() {
    setLoadingCarrierList(true);
    mainActions.setNotWait(true);
    getCarrierListService.handle()
      .then(result => setCarrierList(result?.value?.carriers || []))
      .finally(() => {
        setTimeout(() => mainActions.setNotWait(false), 1000);
        setLoadingCarrierList(false);
      });
  }

}

export { ModalTransportOrderBaseReport };
